import qs from 'qs'
import uniq from 'lodash/uniq'

import type { RoomSelectInfo } from 'core/bookings/types'

/**
 * @function parseHotelPlanCodes
 * Input the value from hotelPlanCodes query param
 * and parse it correctly whether its an array or single value
 * @param hotelPlanCodes
 * @returns
 */
const parseHotelPlanCodes = (
  hotelPlanCodes?: string | null,
): string[] | string | undefined => {
  const parsedHotelPlanCodes = hotelPlanCodes
    ? (qs.parse(hotelPlanCodes) as { [key: string]: string })
    : undefined

  if (!parsedHotelPlanCodes) return undefined

  // it means parsedHotelPlanCodes structure is like this
  // { '0': 'hotelPlanCode1', '1': 'hotelPlanCode2' }
  if (
    Object.keys(parsedHotelPlanCodes).length > 1 ||
    (typeof parsedHotelPlanCodes === 'object' &&
      Object.hasOwn(parsedHotelPlanCodes, '0'))
  ) {
    const values = Object.values(parsedHotelPlanCodes)
    return values
  }

  // it means parsedHotelPlanCodes structure is like this
  // { 'hotelPlanCode1': '' }, should not be { '0': 'hotelPlanCode1' }
  if (
    Object.keys(parsedHotelPlanCodes).length === 1 ||
    (typeof parsedHotelPlanCodes === 'object' &&
      !Object.hasOwn(parsedHotelPlanCodes, '0'))
  ) {
    const firstKey = Object.keys(parsedHotelPlanCodes)[0]
    return firstKey
  }

  return undefined
}

/**
 * @function stringifyHotelPlanCodes
 * Input the value from parsed hotelPlanCodes
 * and stringify it correctly whether its an array or single value
 * @param hotelPlanCodes
 * @returns
 */
const stringifyHotelPlanCodes = (
  hotelPlanCodes?: string[] | string | null,
): string | null => {
  if (!hotelPlanCodes) return null

  if (Array.isArray(hotelPlanCodes)) {
    return qs.stringify(hotelPlanCodes)
  }

  return hotelPlanCodes
}

/**
 * @function checkAndSetResaleHotelPlanCodesQueryParams
 * helper function to determine if we have to set hotelPlanCodes
 * @param isResalePlan
 * @param selectedRoomList
 * @param urlSearchParams
 * @param currentSelectedHotelPlanCodes
 * @returns
 */
const checkAndSetResaleHotelPlanCodesQueryParams = (
  isResalePlan: boolean,
  selectedRoomList: RoomSelectInfo[],
  urlSearchParams: URLSearchParams,
  currentSelectedHotelPlanCodes?: string[] | string,
): void => {
  if (!isResalePlan) return

  const roomHotelPlanCodes = selectedRoomList
    .map((room) => room.hotelPlanCode)
    .filter((code) => !!code) as string[]

  // if type array
  if (Array.isArray(currentSelectedHotelPlanCodes)) {
    const allHotelPlanCodes =
      currentSelectedHotelPlanCodes.concat(roomHotelPlanCodes)

    urlSearchParams.set(
      'hotelPlanCodes',
      stringifyHotelPlanCodes(uniq(allHotelPlanCodes)) as string,
    )

    return
    // if string
  } else if (typeof currentSelectedHotelPlanCodes === 'string') {
    roomHotelPlanCodes.push(currentSelectedHotelPlanCodes)

    urlSearchParams.set(
      'hotelPlanCodes',
      stringifyHotelPlanCodes(uniq(roomHotelPlanCodes)) as string,
    )

    return
    // if undefined
  } else {
    urlSearchParams.set(
      'hotelPlanCodes',
      stringifyHotelPlanCodes(roomHotelPlanCodes) as string,
    )
  }
}

export {
  parseHotelPlanCodes,
  stringifyHotelPlanCodes,
  checkAndSetResaleHotelPlanCodesQueryParams,
}
